import { useEffect, useRef, useState } from 'react';
import { PerformanceSupportDashboardService } from '../services/PerformanceSupportDashboardService';
import type {
  PerformanceSupportDashboard,
  PerformanceSupportDashboardQuery,
  PerformanceSupportResponse,
} from '../utils/performanceSupportDashboard';

const SEARCH_DEBOUNCE_MS = 350;

function areQueriesEqualExceptSearch(
  left: PerformanceSupportDashboardQuery,
  right: PerformanceSupportDashboardQuery,
): boolean {
  return left.page === right.page
    && left.pageSize === right.pageSize
    && left.reasons.length === right.reasons.length
    && left.reasons.every((value, index) => value === right.reasons[index]);
}

export function usePerformanceSupportDashboard(query: PerformanceSupportDashboardQuery) {
  const [snapshot, setSnapshot] = useState<PerformanceSupportDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevQuery = useRef<PerformanceSupportDashboardQuery | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const previous = prevQuery.current;
    const onlySearchChanged = Boolean(
      previous
      && query.searchName !== previous.searchName
      && areQueriesEqualExceptSearch(query, previous),
    );
    prevQuery.current = query;

    const run = () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      setError(null);

      PerformanceSupportDashboardService.getDashboard(query)
        .then((next) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(next);
        })
        .catch((caught: unknown) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(null);
          setError(caught instanceof Error ? caught.message : 'Não foi possível carregar o suporte da performance.');
        })
        .finally(() => {
          if (currentRequest === requestId.current) {
            setLoading(false);
          }
        });
    };

    clearTimeout(debounceTimer.current);
    if (onlySearchChanged) {
      debounceTimer.current = setTimeout(run, SEARCH_DEBOUNCE_MS);
    } else {
      run();
    }

    return () => clearTimeout(debounceTimer.current);
  }, [query]);

  const markResponded = async (supportId: number, response: PerformanceSupportResponse) => {
    const previous = snapshot;
    setSnapshot((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        students: current.students.map((row) => {
          if (row.id !== supportId) {
            return row;
          }

          if (response === 'yes') {
            return {
              ...row,
              response,
              respondedAt: row.respondedAt ?? new Date().toISOString(),
            };
          }

          return { ...row, response, respondedAt: null };
        }),
      };
    });

    try {
      const saved = await PerformanceSupportDashboardService.markResponded(supportId, response);
      setSnapshot((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          students: current.students.map((row) => (
            row.id === supportId
              ? { ...row, response: saved.response, respondedAt: saved.respondedAt }
              : row
          )),
        };
      });
    } catch (caught: unknown) {
      setSnapshot(previous);
      throw caught;
    }
  };

  return { snapshot, loading, error, markResponded };
}
